import { FiCheck, FiX, FiArrowLeft } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMemo } from "react";

export default function ComparisonPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const highlight = params.get("highlight"); // free | pro | premium

  const colClass = (col) =>
    col === highlight
      ? "bg-blue-50 border-blue-300 border-l-2 border-r-2 font-semibold"
      : "";

  // --------------------------------------------
  // 1️⃣ RAW FEATURE ROWS (YOUR EXACT CONTENT)
  // --------------------------------------------
  const rows = [
    // CORE
    { f: "Schedule", free: true, pro: true, premium: true },
    { f: "Classes", free: true, pro: true, premium: true },
    { f: "Scoreboard", free: true, pro: true, premium: true },

    { f: "Training Log", free: true, pro: true, premium: true },
    { f: "Fitness Log", free: true, pro: true, premium: true },

    { f: "Basic Analytics", free: false, pro: true, premium: true },
    { f: "Advanced Analytics", free: false, pro: false, premium: true },

    { f: "Player Insights", free: false, pro: true, premium: true },

    { f: "Coach Assigned Periodisation", free: true, pro: true, premium: true },
    { f: "Personal Periodisation Plan", free: false, pro: false, premium: true },

    { f: "Book a Coach", free: true, pro: true, premium: true },

    // Competitive
    { f: "Leaderboard", free: false, pro: true, premium: true },
    { f: "Internal Academy Ranking", free: false, pro: true, premium: true },
    { f: "Player Rating", free: false, pro: false, premium: true },
    { f: "Match History", free: true, pro: true, premium: true },
    { f: "Win / Loss History", free: false, pro: true, premium: true },
    { f: "Head-to-head Stats", free: false, pro: false, premium: true },

    // Development
    { f: "Footwork / Ghosting Counter", free: false, pro: false, premium: true },
    { f: "Ghosting Timer", free: false, pro: true, premium: true },
    { f: "Weekly Self-Assessment", free: true, pro: true, premium: true },
    { f: "Monthly Self-Assessment", free: false, pro: false, premium: true },
    { f: "Game Plan Builder", free: false, pro: false, premium: true },
    { f: "Opponent Notebook", free: false, pro: false, premium: true },
    { f: "Consistency Tracker", free: false, pro: true, premium: true },
    { f: "Drill Frequency Counter", free: false, pro: true, premium: true },

    // Lifestyle
    { f: "Racket Bag Checklist", free: true, pro: true, premium: true },
    { f: "Gear Maintenance Log", free: false, pro: true, premium: true },
    { f: "Squash Expense Tracker", free: false, pro: false, premium: true },
  ];

  // --------------------------------------------
  // 2️⃣ SORTING LOGIC (✓ rows → X rows → text rows)
  // --------------------------------------------
  const sortedRows = useMemo(() => {
    const score = (v) =>
      v === true ? 2 : v === false || v === undefined ? 1 : 0; // text = 0 lowest

    return [...rows].sort((a, b) => {
      const sA = score(a.free) + score(a.pro) + score(a.premium);
      const sB = score(b.free) + score(b.pro) + score(b.premium);
      return sB - sA; // higher score first
    });
  }, [rows]);

  return (
    <div className="flex-grow w-full bg-white py-8 px-6 md:px-16 lg:px-24">

      <h1 className="mb-4 text-center">Plan Comparison</h1>

      {/* Back */}
      <div className="max-w-5xl mx-auto mb-4">
        <button
          onClick={() => navigate("/pricing")}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition font-medium"
        >
          <FiArrowLeft /> Back to Pricing
        </button>
      </div>

      {/* Table */}
      <div className="max-w-5xl mx-auto overflow-x-auto">
        <table className="w-full border-collapse">

          {/* Header */}
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left font-semibold w-[40%]">Features</th>
              <th className={`p-3 text-center font-semibold w-[20%] ${colClass("free")}`}>Free</th>
              <th className={`p-3 text-center font-semibold w-[20%] ${colClass("pro")}`}>Pro</th>
              <th className={`p-3 text-center font-semibold w-[20%] ${colClass("premium")}`}>Premium</th>
            </tr>
          </thead>

          <tbody className="text-gray-700 text-sm md:text-base">
            {sortedRows.map((r, i) => (
              <Row
                key={i}
                feature={r.f}
                free={r.free}
                pro={r.pro}
                premium={r.premium}
                highlight={highlight}
              />
            ))}
          </tbody>

        </table>
      </div>

      <p className="text-center text-sm text-gray-600 mt-6">
        Contact us to subscribe or upgrade.
      </p>
    </div>
  );
}

const Row = ({ feature, free, pro, premium, highlight }) => {
  const colClass = (col) =>
    col === highlight ? "bg-blue-50 font-semibold border-blue-300 border-l-2 border-r-2" : "";

  return (
    <tr className="border-b">
      <td className="p-3 text-left">{feature}</td>
      <td className={`p-3 text-center ${colClass("free")}`}>{render(free)}</td>
      <td className={`p-3 text-center ${colClass("pro")}`}>{render(pro)}</td>
      <td className={`p-3 text-center ${colClass("premium")}`}>{render(premium)}</td>
    </tr>
  );
};

const render = (value) => {
  if (value === true) return <FiCheck className="text-green-600 inline" />;
  if (value === false || value === undefined)
    return <FiX className="text-red-500 inline" />;
  return <span className="text-gray-700">{value}</span>;
};
